import React from 'react';
import { useSelector } from 'react-redux';
import { useImmerReducer } from 'use-immer';
import isEqual from 'lodash/isEqual';
import slugify from 'slugify';
import {
	Accordion,
	Button,
	CloseButton,
	Dialog,
	Flex,
	Heading,
	HStack,
	Input,
	NumberInput,
	Portal,
	NativeSelect,
	Spacer,
	Switch,
	Text,
	Field,
	useDisclosure,
	Icon
} from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import theme from '../theme';
import moment from 'moment';

const buildNewState = (formState, field, key, fields = {}, parent) => {
	const childFields = Object.keys(field).filter(_field => _field[0] !== '#');
	const type = field['#type'];

	if (!key) {
		fields.approval_timestamp = null;
	}

	if (type === 'webform_section') {
		fields[key] = {
			categories: {}
		};

		if (Object.prototype.hasOwnProperty.call(field, '#addons')) {
			fields[key].addons = formState?.[key]?.addons || [];
		}

		if (Object.prototype.hasOwnProperty.call(field, '#unit')) {
			fields[key].unit = field['#unit'] ?? '';
		}
	}

	if (type === 'item') {
		const measure = field['#measure'] ?? false;
		const unit = field['#unit'] ?? '';
		let billable = field['#billable'] ?? false;

		if (Object.prototype.hasOwnProperty.call(field, '#Billable')) {
			billable = field['#Billable'];
		}

		const categories = field['#categories'] ?? [];
		const select = field['#select'] ?? false;
		const input_list = field['#input_list'] ?? false;

		categories.forEach(cat => {
			const slug = slugify(cat, {
				replacement: '_',
				lower: true,
				remove: /[*+~.()'"!:@]/g
			});

			if (!Object.prototype.hasOwnProperty.call(fields[parent].categories, 'slug')) {
				fields[parent].categories[slug] = {
					name: cat,
					checked: formState[parent]?.categories?.[slug]?.checked || false,
					items: [key]
				};
			} else {
				fields[parent].categories[slug].items.push(key);
			}
		});

		const part_one = { checked: false };
		const part_two = { checked: false };

		if (measure) {
			part_one.measure = '';
			part_two.measure = '';
		}

		fields[parent][key] = {
			unit: formState[parent]?.[key]?.unit || unit,
			billable: formState[parent]?.[key]?.billable || billable,
			part_one: formState[parent]?.[key]?.part_one || part_one,
			part_two: formState[parent]?.[key]?.part_two || part_two
		};

		if (select) {
			if (select.multi) {
				fields[parent][key].items = [];
			} else {
				fields[parent][key].selection = select.default || '';
			}
		}

		if (input_list) {
			fields[parent][key].items = [];
		}
	}

	if (childFields) {
		childFields.forEach(x => buildNewState(formState, field[x], x, fields, key));
	}

	return fields;
};

function formReducer(draft, action) {
	switch (action.type) {
		case 'new_updater': {
			const { category, field, part, checked, measure } = action.payload;

			if (typeof checked === 'boolean') {
				draft.fields[category][field][part].checked = checked;
			}

			if (measure) {
				draft.fields[category][field][part].measure = measure;
			}

			draft.fields.approval_timestamp = null;

			break;
		}
		case 'select_subcategory': {
			const { category, subCategory, checked } = action.payload;
			draft.fields[category].categories[subCategory].checked = checked;
			draft.fields.approval_timestamp = null;

			break;
		}
		case 'update_addon_state': {
			const { category, index, part, checked, measure } = action.payload;

			if (typeof checked === 'boolean') {
				draft.fields[category].addons[index][part].checked = checked;
			}

			if (measure) {
				draft.fields[category].addons[index][part].measure = measure;
			}

			draft.fields.approval_timestamp = null;

			break;
		}
		case 'update_addon_field': {
			draft.fields[action.payload.category].addons[action.payload.index][action.payload.field] =
				action.payload.value;
			draft.fields.approval_timestamp = null;
			break;
		}
		case 'update': {
			const payload = Array.isArray(action.payload) ? action.payload : [action.payload];

			payload.forEach(update => {
				const { field, value, index } = update;

				if (!isNaN(index)) {
					draft.fields[field][index][`${field}_item`] = value;
				} else {
					draft.fields[field] = value;
				}
			});

			draft.fields.approval_timestamp = null;
			break;
		}
		case 'approve_form': {
			draft.fields.approval_timestamp = new Date().toString();

			break;
		}
		case 'reset_form': {
			draft.fields.approval_timestamp = '';
			for (const category in draft.fields) {
				for (const top_level_category in draft.fields[category]) {
					if (draft.fields[category][top_level_category].part_one) {
						draft.fields[category][top_level_category] = {
							...draft.fields[category][top_level_category],
							part_one: { checked: false },
							part_two: { checked: false }
						};
					} else if (top_level_category === 'categories') {
						draft.fields[category][top_level_category] = Object.values(
							draft.fields[category][top_level_category]
						).map(subCategoryObject => ({
							...subCategoryObject,
							checked: false
						}));
					} else if (top_level_category === 'addons') {
						draft.fields[category][top_level_category] = draft.fields[category][top_level_category].map(
							addon => ({
								...addon,
								part_one: { checked: false },
								part_two: { checked: false }
							})
						);
					}
				}
			}

			break;
		}
		case 'add_repeater_set': {
			draft.fields.approval_timestamp = null;
			draft.fields[action.payload.key].push(action.payload.entry);

			break;
		}
		case 'update_multi_item': {
			const { category, item, index, part, values } = action.payload;
			draft.fields.approval_timestamp = null;

			values.forEach(val => {
				if (part) {
					draft.fields[category][item].items[index][part][val.field] = val.value;
				} else {
					draft.fields[category][item].items[index][val.field] = val.value;
				}
			});

			break;
		}
		case 'add_multi_item': {
			const { category, item, measure } = action.payload;
			draft.fields.approval_timestamp = null;
			draft.fields[category][item].items.push({
				part_one: {
					checked: false,
					measure: measure ? '' : false
				},
				part_two: {
					checked: false,
					measure: measure ? '' : false
				},
				selection: '',
				name: ''
			});

			break;
		}
		case 'add_addon_item': {
			draft.fields.approval_timestamp = null;
			draft.fields[action.payload.category].addons.push({
				part_one: {
					checked: false,
					measure: ''
				},
				part_two: {
					checked: false,
					measure: ''
				},
				name: '',
				unit_code: '',
				billable: false
			});

			break;
		}
		case 'remove_addon_item': {
			const { category, index } = action.payload;

			const list = draft.fields[category].addons;
			list.splice(index, 1);
			draft.fields[category].addons = list;

			draft.fields.approval_timestamp = null;

			break;
		}
		default:
			throw new Error();
	}
}

export default ({ currentForm, dataAPI, edit, formData, formDefinition, saveForm, isSubmitted }) => {
	const [state, dispatch] = useImmerReducer(formReducer, { fields: formData });

	const [addonDialogOpen, setAddonDialogOpen] = React.useState(null);

	const updatedFormDefinition = useSelector(state => state.webforms.forms.find(({ id }) => id === formDefinition.id));

	const cancelResetRef = React.useRef(null);
	const addonRefs = {};

	const { isOpen, onOpen, onClose } = useDisclosure();

	const [[initialSection]] = React.useMemo(
		() =>
			Object.entries(formDefinition.elements.form_root).filter(
				([, value]) => value['#type'] === 'webform_section'
			),
		[formDefinition.elements.form_root]
	);

	const unitTable = React.useMemo(() => {
		const result = dataAPI.getPhaseValue('unit_table');
		return Array.isArray(result) ? result.sort((a, b) => (a.name < b.name ? -1 : 1)) : [];
	}, [dataAPI]);

	const validAddons = React.useMemo(
		() =>
			// Filter categories for those with addons with no unit codes,
			// then return as valid if none are found
			!Object.values(state.fields).filter(
				category =>
					category &&
					!Array.isArray(category) &&
					typeof category === 'object' &&
					// get the length of filtered categories with addons with no unit codes
					Object.entries(category).filter(
						// only filter via checking if addons exist and they contain any with no unit codes
						([key, addons]) =>
							key === 'addons' &&
							addons.find(
								addon =>
									Object.prototype.hasOwnProperty.call(addon, 'unit_code') &&
									Object.prototype.hasOwnProperty.call(addon, 'billable') &&
									!addon.unit_code &&
									addon.billable
							)
					).length
			).length,
		[state.fields]
	);

	const updateDefinition = React.useMemo(
		() => !isSubmitted && updatedFormDefinition && !isEqual(updatedFormDefinition, formDefinition),
		[isSubmitted, updatedFormDefinition, formDefinition]
	);

	React.useEffect(() => {
		if (updateDefinition) {
			const newState = buildNewState({ ...state.fields }, updatedFormDefinition.elements.form_root);

			saveForm({
				...currentForm,
				form_data: newState,
				form_definition: JSON.stringify(updatedFormDefinition)
			});
		} else {
			saveForm({
				...currentForm,
				form_data: state.fields
			});
		}
	}, [state.fields, updateDefinition]);

	const renderAddons = (category, addons) =>
		addons.map((addon, index) => (
			<Flex
				key={index}
				bgColor={index % 2 === 0 ? theme.colors.gray[500] : theme.colors.white}
				flexDirection="column"
			>
				<Flex alignItems="center">
					<Flex flex="1">
						<Flex flex="1" p="8px" justify="center" align="center" position="relative">
							<Switch.Root
								colorPalette="blue"
								size="lg"
								onCheckedChange={e =>
									dispatch({
										type: 'update_addon_state',
										payload: {
											category,
											index,
											part: 'part_one',
											checked: e.checked
										}
									})
								}
								checked={addon.part_one?.checked ?? false}
								disabled={!window.isSupervisor && isSubmitted && !edit}
							>
								<Switch.HiddenInput />
								<Switch.Control />
							</Switch.Root>
							<NumberInput.Root
								position="absolute"
								left="calc(50% + 33px)"
								w="60px"
								pl="8px"
								onValueChange={e =>
									dispatch({
										type: 'update_addon_state',
										payload: {
											category,
											index,
											part: 'part_one',
											measure: e.value
										}
									})
								}
								value={addon.part_one.measure}
								disabled={isSubmitted && !edit}
							>
								<NumberInput.Input
									fontSize={16}
									p="6px"
									h="29px"
									bgColor={theme.colors.white}
									borderRadius="base"
									_disabled={{
										opacity: 1,
										bgColor: theme.colors.gray[400],
										color: theme.colors.black
									}}
								/>
							</NumberInput.Root>
						</Flex>
						<Flex flex="1" p="8px" justify="center" align="center" position="relative">
							<Switch.Root
								colorPalette="blue"
								size="lg"
								onCheckedChange={e =>
									dispatch({
										type: 'update_addon_state',
										payload: {
											category,
											index,
											part: 'part_two',
											checked: e.checked
										}
									})
								}
								checked={addon.part_two?.checked ?? false}
								disabled={isSubmitted && !edit}
							>
								<Switch.HiddenInput />
								<Switch.Control />
							</Switch.Root>
							<NumberInput.Root
								position="absolute"
								left="calc(50% + 33px)"
								w="60px"
								pl="8px"
								onValueChange={e =>
									dispatch({
										type: 'update_addon_state',
										payload: {
											category,
											index,
											part: 'part_two',
											measure: e.value
										}
									})
								}
								value={addon.part_two.quantity}
								disabled={isSubmitted && !edit}
							>
								<NumberInput.Input
									fontSize={16}
									p="6px"
									h="29px"
									bgColor={theme.colors.white}
									borderRadius="base"
									_disabled={{
										opacity: 1,
										bgColor: theme.colors.gray[400],
										color: theme.colors.black
									}}
								/>
							</NumberInput.Root>
						</Flex>
					</Flex>
					<Flex flex="2" py="8px" mx="16px" alignItems="center">
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							p={4}
							value={addon.name}
							onChange={({ target }) => {
								dispatch({
									type: 'update_addon_field',
									payload: {
										category,
										index,
										field: 'name',
										value: target.value
									}
								});
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
						<Flex flexDirection="column" flexGrow="0" ml="12px">
							<Text fontSize={16}>Unit Code</Text>
							<Field.Root invalid={addon.billable && !addon.unit_code}>
								<NativeSelect.Root size="sm">
									<NativeSelect.Field
										fontSize={16}
										placeholder="Select an item..."
										textOverflow="ellipsis"
										whiteSpace="nowrap"
										overflow="hidden"
										h="18px"
										bgColor={theme.colors.white}
										borderRadius="base"
										value={addon.unit_code}
										onChange={({ target }) => {
											dispatch({
												type: 'update_addon_field',
												payload: {
													category,
													index,
													field: 'unit_code',
													value: target.value
												}
											});
										}}
										disabled={isSubmitted && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									>
										{unitTable.map(({ name, unit }, _index) => (
											<option key={_index} value={unit}>
												{name}
											</option>
										))}
									</NativeSelect.Field>
									<NativeSelect.Indicator />
								</NativeSelect.Root>
							</Field.Root>
						</Flex>
						<Flex flexDirection="column" flexGrow="0" mx="12px">
							<Text fontSize={16}>Billable</Text>
							<Switch.Root
								size="lg"
								onCheckedChange={e =>
									dispatch({
										type: 'update_addon_field',
										payload: {
											category,
											index,
											field: 'billable',
											value: e.checked
										}
									})
								}
								checked={typeof addon.billable === 'boolean' ? addon.billable : false}
								disabled={isSubmitted && !edit}
							>
								<Switch.HiddenInput />
								<Switch.Control />
							</Switch.Root>
						</Flex>
						{(!isSubmitted || edit) && (
							<>
								<CloseButton
									size="md"
									color={theme.colors.white}
									bgColor={theme.colors.red[500]}
									_hover={{ background: theme.colors.red[600] }}
									disabled={isSubmitted && !edit}
									onClick={() => setAddonDialogOpen(category + index)}
								/>
								<Dialog.Root
									role="alertdialog"
									initialFocusEl={addonRefs?.category?.[index]}
									onOpenChange={({ open }) => {
										if (!open) {
											setAddonDialogOpen(null);
										}
									}}
									open={addonDialogOpen === category + index}
									placement="center"
								>
									<Portal>
										<Dialog.Backdrop />
										<Dialog.Positioner>
											<Dialog.Content opacity="1 !important">
												<Dialog.Header>
													<Dialog.Title fontSize="lg" fontWeight="bold">
														Confirm Delete
													</Dialog.Title>
												</Dialog.Header>
												<Dialog.Body>
													<p>Are you sure you want to remove this item?</p>
												</Dialog.Body>
												<Dialog.Footer>
													<Dialog.ActionTrigger asChild>
														<Button
															size="lg"
															ref={ref => {
																if (
																	!Object.prototype.hasOwnProperty.call(
																		addonRefs,
																		category
																	)
																) {
																	addonRefs.category = [];
																} else {
																	addonRefs.category[index] = ref;
																}
															}}
															onClose={() => setAddonDialogOpen(null)}
														>
															Cancel
														</Button>
													</Dialog.ActionTrigger>
													<Button
														colorPalette="red"
														size="lg"
														ml={3}
														bg={theme.colors.red[500]}
														_hover={{ background: theme.colors.red[600] }}
														color={theme.colors.white}
														onClick={() => {
															setAddonDialogOpen(null);
															dispatch({
																type: 'remove_addon_item',
																payload: {
																	category,
																	index
																}
															});
														}}
													>
														Delete Item
													</Button>
												</Dialog.Footer>
												<Dialog.CloseTrigger asChild>
													<CloseButton size="sm" />
												</Dialog.CloseTrigger>
											</Dialog.Content>
										</Dialog.Positioner>
									</Portal>
								</Dialog.Root>
							</>
						)}
					</Flex>
				</Flex>
			</Flex>
		));

	function renderFields(field = {}, key, parent, index) {
		const childFields = Object.keys(field).filter(_field => _field[0] !== '#');
		const type = field['#type'];

		switch (type) {
			case 'container': {
				return (
					<Accordion.Root multiple collapsible defaultValue={[childFields.indexOf(initialSection) - 1]}>
						{childFields.map((category, _index) => renderFields(field[category], category, key, _index))}
					</Accordion.Root>
				);
			}
			case 'webform_section': {
				const addons = field['#addons'] ?? false;
				const hasCategory = {};

				// was introduced in 6ae599d70167084e0f14d8fda607b38215466a3b - 'loadout list fix' in msi.forms-app
				if (Object.prototype.hasOwnProperty.call(state.fields, key)) {
					const categories = Object.entries(state.fields[key].categories).map(([_index, category]) => {
						category.items.forEach(item => (hasCategory[item] = true));

						return {
							key: _index,
							...category
						};
					});

					const renderCategories = categories.reduce((acc, category) => {
						acc[category.name] = {
							key: category.key,
							name: category.name
						};

						return acc;
					}, {});

					const uncategorized = childFields.filter(
						childField => !Object.prototype.hasOwnProperty.call(hasCategory, childField)
					);

					// Check if supervisor has checked 'needed'
					const supervisorChecked = Object.entries(state.fields[key]).reduce(
						(acc, [loadoutKey, loadoutItem]) => {
							if (acc) {
								return true;
							} else if (loadoutKey === 'categories') {
								return Object.values(loadoutItem).reduce((_acc, { checked }) => _acc || checked, false);
							} else {
								return Object.prototype.hasOwnProperty.call(loadoutItem, 'part_one')
									? loadoutItem.part_one.checked
									: false;
							}
						},
						false
					);

					// Check if crew member has checked 'loaded'
					const teamCheck = Object.entries(state.fields[key]).reduce((acc, [loadoutKey, loadoutItem]) => {
						if (
							acc === 2 ||
							(loadoutKey !== 'categories' &&
								loadoutItem &&
								typeof loadoutItem === 'object' &&
								Object.prototype.hasOwnProperty.call(loadoutItem, 'part_one') &&
								Object.prototype.hasOwnProperty.call(loadoutItem, 'part_two') &&
								loadoutItem.part_one.checked &&
								!loadoutItem.part_two.checked)
						) {
							// return 2 if acc is already 2, or if needed is checked,
							// but loaded is not checked by team member
							return 2;
						}

						if (loadoutKey === 'categories') {
							const uncheckedItems = Object.values(loadoutItem)
								// filter out the category objects where the category is checked
								// and all items of said category are not checked by the team members
								.filter(
									({ checked, items }) =>
										// if our category is checked
										checked &&
										// and if all items in the current category are unchecked
										items
											// return items for our current category
											.map(item =>
												Object.entries(state.fields[key]).filter(
													([_loadoutKey]) => _loadoutKey === item
												)
											)
											// to see if every one of those items are unchecked
											.every(
												([
													[
														,
														{
															part_two: { checked: _checked = false }
														}
													]
													// example - checked: [false, false, false, false] => !false => true
												]) => !_checked
											)
								);

							// return 2 (exclamation) for categories are checked with all unchecked items from team members
							if (uncheckedItems.length) {
								return 2;
							}
						}

						if (
							// Make sure we're dealing with a valid loadout item
							loadoutItem &&
							typeof loadoutItem === 'object' &&
							Object.prototype.hasOwnProperty.call(loadoutItem, 'part_one') &&
							Object.prototype.hasOwnProperty.call(loadoutItem, 'part_two')
						) {
							if (!loadoutItem.part_one.checked && loadoutItem.part_two.checked) {
								// if needed is unchecked, but loaded is checked
								const categoryChecked = Object.entries(state.fields[key])
									.filter(([_loadoutKey]) => _loadoutKey === 'categories')
									.some(([, _loadoutItem]) =>
										Object.values(_loadoutItem).some(
											({ checked, items }) => checked && !!items.find(item => item === loadoutKey)
										)
									);

								const anyCategoryChecked = Object.entries(state.fields[key])
									.filter(([_loadoutKey]) => _loadoutKey === 'categories')
									.some(([, _loadoutItem]) =>
										Object.values(_loadoutItem).find(({ checked }) => checked)
									);

								if (categoryChecked) {
									return 1;
								} else if (anyCategoryChecked) {
									return 2;
								} else {
									const supervisorCheckedOther = Object.entries(state.fields[key])
										.filter(
											([_loadoutKey]) => !(_loadoutKey === 'categories' || _loadoutKey === 'k')
										)
										.some(([[, _loadoutItem]]) => _loadoutItem?.part_one?.checked || false);

									return supervisorCheckedOther ? 2 : acc;
								}
							} else if (loadoutItem.part_one.checked && loadoutItem.part_two.checked) {
								// everything is normal, yay!
								return 1;
							} else {
								return acc;
							}
						}
					}, 0);

					let teamIndicator;

					switch (teamCheck) {
						case 2:
							teamIndicator = (
								<Icon w="29px" h="29px" boxSize="29px" color="yellow.500">
									<FaExclamationCircle />
								</Icon>
							);
							break;
						case 1:
							teamIndicator = (
								<Icon w="29px" h="29px" boxSize="29px" color="green.500">
									<FaCheckCircle />
								</Icon>
							);
							break;
						default:
							break;
					}

					return (
						<Accordion.Item key={key} value={key}>
							<Flex flexDirection="column">
								<Flex alignItems="center">
									<Flex flex="1">
										<Flex flex="1" p="8px" justify="center" align="center">
											{supervisorChecked ? (
												<Icon w="29px" h="29px" boxSize="29px" color="green.500">
													<FaCheckCircle />
												</Icon>
											) : (
												<Spacer w="29px" h="29px" />
											)}
										</Flex>
										<Flex flex="1" p="8px" justify="center" align="center">
											{teamCheck > 0 ? teamIndicator : <Spacer w="29px" h="29px" />}
										</Flex>
									</Flex>
									<Flex flex="2">
										<Accordion.ItemTrigger>
											<Accordion.ItemIndicator />
											<Text fontSize={20} fontWeight="bold">
												{field['#title']}
											</Text>
										</Accordion.ItemTrigger>
									</Flex>
								</Flex>
							</Flex>
							<Accordion.ItemContent pb={4} pl={0} pr={0} pt={0}>
								<Accordion.ItemBody>
									<Accordion.Root collapsible defaultValue={[0]}>
										{/*Subcategories and items*/}
										{Object.values(renderCategories).map(category => {
											const _category = categories.find(
												_category => _category.key === category.key
											);
											const items = _category ? _category.items : false;

											return (
												<Accordion.Item key={category.key} value={category.key}>
													<Flex flexDirection="column">
														<Flex alignItems="center">
															<Flex flex="1">
																<Flex flex="1" p="8px" justify="center" align="center">
																	<Switch.Root
																		size="lg"
																		id={`${key}-${category.name}`}
																		onCheckedChange={e =>
																			dispatch({
																				type: 'select_subcategory',
																				payload: {
																					subCategory: category.key,
																					category: key,
																					checked: e.checked
																				}
																			})
																		}
																		checked={
																			state.fields[key].categories[category.key]
																				?.checked ?? false
																		}
																		disabled={
																			!window.isSupervisor && isSubmitted && !edit
																		}
																	>
																		<Switch.HiddenInput />
																		<Switch.Control />
																	</Switch.Root>
																</Flex>
																<Flex flex="1" p="8px" justify="center" align="center">
																	<Spacer w="29px" h="29px" />
																</Flex>
															</Flex>
															<Flex flex="2">
																<Accordion.ItemTrigger>
																	<Accordion.ItemIndicator />
																	<Text fontSize={20}>{category.name}</Text>
																</Accordion.ItemTrigger>
															</Flex>
														</Flex>
													</Flex>
													<Accordion.ItemContent px={0} pt={0} pb={4}>
														<Accordion.ItemBody>
															{items?.map((item, _index) =>
																renderFields(field[item], item, key, _index)
															)}
														</Accordion.ItemBody>
													</Accordion.ItemContent>
												</Accordion.Item>
											);
										})}
									</Accordion.Root>
									{/*Category items*/}
									{uncategorized.map((category, _index) =>
										renderFields(field[category], category, key, _index)
									)}
									{/*addons*/}
									{addons && state.fields[key].addons ? (
										<Flex flexDirection="column">
											{renderAddons(key, state.fields[key].addons, childFields.length)}
											<Button
												size="lg"
												color={theme.colors.white}
												bg={theme.colors.blue[700]}
												_hover={{ backgroundColor: theme.colors.blue[800] }}
												mt={2}
												disabled={isSubmitted && !edit}
												onClick={() => {
													dispatch({
														type: 'add_addon_item',
														payload: {
															category: key
														}
													});
												}}
											>
												{`Add ${key.split('_').join(' ')}`}
											</Button>
										</Flex>
									) : null}
								</Accordion.ItemBody>
							</Accordion.ItemContent>
						</Accordion.Item>
					);
				}
				break;
			}
			case 'item': {
				// Removing select here due to no select items existing
				const measure = field['#measure'] ?? false;
				const input_list = field['#input_list'] ?? false;

				if (Object.prototype.hasOwnProperty.call(state.fields[parent], key)) {
					if (input_list) {
						return (
							<>
								{state.fields[parent][key].items.map((item, _index) => (
									<Flex
										flex="1"
										key={_index}
										bgColor={_index % 2 === 0 ? theme.colors.gray[500] : theme.colors.white}
										flexDirection="column"
									>
										<Flex alignItems="center">
											<Flex flex="1">
												<Flex flex="1" p="8px" justify="center" align="center">
													<Switch.Root
														size="lg"
														onCheckedChange={e =>
															dispatch({
																type: 'update_multi_item',
																payload: {
																	category: parent,
																	item: key,
																	part: 'part_one',
																	index: _index,
																	values: [
																		{
																			field: 'checked',
																			value: e.checked
																		}
																	]
																}
															})
														}
														checked={
															state.fields[parent][key].items[_index].part_one?.checked ??
															false
														}
														disabled={!window.isSupervisor && isSubmitted && !edit}
													>
														<Switch.HiddenInput />
														<Switch.Control />
													</Switch.Root>
													{input_list.measure && (
														<Flex pl="8px" justify="center" align="center">
															<NumberInput.Root
																w="60px"
																pr={2}
																onValueChange={e =>
																	dispatch({
																		type: 'update_multi_item',
																		payload: {
																			category: parent,
																			item: key,
																			part: 'part_one',
																			index: _index,
																			values: [
																				{
																					field: 'measure',
																					value: e.value
																				}
																			]
																		}
																	})
																}
																value={
																	state.fields[parent][key].items[_index].part_one
																		?.measure
																}
																disabled={!window.isSupervisor && isSubmitted && !edit}
															>
																<NumberInput.Input
																	fontSize={16}
																	p="6px"
																	h="29px"
																	bgColor={theme.colors.white}
																	borderRadius="base"
																	_disabled={{
																		opacity: 1,
																		bgColor: theme.colors.gray[400],
																		color: theme.colors.black
																	}}
																/>
															</NumberInput.Root>
															<Text fontWeight="bold" fontSize={16}>
																{input_list.measure}
															</Text>
														</Flex>
													)}
												</Flex>
												<Flex flex="1" p="8px" justify="center" align="center">
													<Switch.Root
														size="lg"
														onCheckedChange={e =>
															dispatch({
																type: 'update_multi_item',
																payload: {
																	category: parent,
																	item: key,
																	part: 'part_one',
																	index: _index,
																	values: [
																		{
																			field: 'checked',
																			value: e.checked
																		}
																	]
																}
															})
														}
														checked={
															state.fields[parent][key].items[_index].part_one?.checked ??
															false
														}
														disabled={!window.isSupervisor && isSubmitted && !edit}
													>
														<Switch.HiddenInput />
														<Switch.Control />
													</Switch.Root>
													{input_list.measure && (
														<Flex pl="8px" justify="center" align="center">
															<NumberInput.Root
																w="60px"
																pr={2}
																onValueChange={e =>
																	dispatch({
																		type: 'update_multi_item',
																		payload: {
																			category: parent,
																			item: key,
																			part: 'part_two',
																			index: _index,
																			values: [
																				{
																					field: 'measure',
																					value: e.value
																				}
																			]
																		}
																	})
																}
																value={
																	state.fields[parent][key].items[_index].part_two
																		.measure
																}
																disabled={isSubmitted && !edit}
															>
																<NumberInput.Input
																	fontSize={16}
																	p="6px"
																	h="29px"
																	bgColor={theme.colors.white}
																	borderRadius="base"
																	_disabled={{
																		opacity: 1,
																		bgColor: theme.colors.gray[400],
																		color: theme.colors.black
																	}}
																/>
															</NumberInput.Root>
															<Text fontWeight="bold" fontSize={16}>
																{input_list.measure}
															</Text>
														</Flex>
													)}
												</Flex>
											</Flex>
											<Flex flex="2">
												<Input
													fontSize={16}
													mx="16px"
													bgColor={theme.colors.white}
													p={3}
													flex="1"
													borderRadius="base"
													value={state.fields[parent][key].items[_index].name}
													placeholder={input_list.placeholder}
													onChange={({ target }) =>
														dispatch({
															type: 'update_multi_item',
															payload: {
																category: parent,
																item: key,
																index: _index,
																values: [
																	{
																		field: 'name',
																		value: target.value
																	}
																]
															}
														})
													}
													disabled={isSubmitted && !edit}
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</Flex>
										</Flex>
									</Flex>
								))}
								<Button
									size="lg"
									color={theme.colors.white}
									bg={theme.colors.blue[700]}
									_hover={{ backgroundColor: theme.colors.blue[800] }}
									disabled={isSubmitted && !edit}
									fontWeight="bold"
									w="100%"
									mt={2}
									onClick={() =>
										dispatch({
											type: 'add_multi_item',
											payload: {
												category: parent,
												item: key,
												measure: input_list.measure
											}
										})
									}
								>
									Add Item
								</Button>
							</>
						);
					} else {
						return (
							<Flex
								key={parent + key + index}
								flexDirection="column"
								bgColor={index % 2 === 0 ? theme.colors.gray[500] : theme.colors.white}
							>
								<Flex alignItems="center">
									<Flex flex="1">
										<Flex flex="1" p="8px" justify="center" align="center">
											<Switch.Root
												size="lg"
												onCheckedChange={e =>
													dispatch({
														type: 'new_updater',
														payload: {
															category: parent,
															field: key,
															part: 'part_one',
															checked: e.checked
														}
													})
												}
												checked={state.fields[parent][key].part_one.checked ?? false}
												disabled={!window.isSupervisor && isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											{measure && (
												<Flex pl="8px" justify="center" align="center">
													<NumberInput.Root
														w="60px"
														pr={2}
														onValueChange={e =>
															dispatch({
																type: 'new_updater',
																payload: {
																	category: parent,
																	field: key,
																	part: 'part_one',
																	measure: e.value
																}
															})
														}
														value={state.fields[parent][key].part_one.measure}
														disabled={!window.isSupervisor && isSubmitted && !edit}
													>
														<NumberInput.Input
															fontSize={16}
															p="6px"
															h="29px"
															bgColor={theme.colors.white}
															borderRadius="base"
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														/>
													</NumberInput.Root>
													<Text fontWeight="bold" fontSize={16}>
														{measure}
													</Text>
												</Flex>
											)}
										</Flex>
										<Flex flex="1" p="8px" justify="center" align="center">
											<Switch.Root
												size="lg"
												onCheckedChange={e =>
													dispatch({
														type: 'new_updater',
														payload: {
															category: parent,
															field: key,
															part: 'part_two',
															checked: e.checked
														}
													})
												}
												checked={state.fields[parent][key].part_two.checked ?? false}
												disabled={isSubmitted && !edit}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											{measure && (
												<Flex pl="8px" justify="center" align="center">
													<NumberInput.Root
														w="60px"
														pr={2}
														onValueChange={e =>
															dispatch({
																type: 'new_updater',
																payload: {
																	category: parent,
																	field: key,
																	part: 'part_two',
																	measure: e.value
																}
															})
														}
														value={state.fields[parent][key].part_two.measure}
														disabled={isSubmitted && !edit}
													>
														<NumberInput.Input
															fontSize={16}
															p="6px"
															h="29px"
															bgColor={theme.colors.white}
															borderRadius="base"
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														/>
													</NumberInput.Root>
													<Text fontWeight="bold" fontSize={16}>
														{measure}
													</Text>
												</Flex>
											)}
										</Flex>
									</Flex>
									<Flex flex="2">
										<Text fontSize={16} px="16px">
											{field['#title']}
										</Text>
									</Flex>
								</Flex>
							</Flex>
						);
					}
				}
				break;
			}
			default:
				break;
		}
	}

	const formTree = renderFields(formDefinition.elements.form_root);

	return (
		<Flex flexDirection="column">
			<Flex alignItems="center">
				<Flex flex="1">
					<Flex flex="1" p="8px" bgColor={theme.colors.gray[500]} justify="center">
						<Heading size="md">Needed</Heading>
					</Flex>
					<Flex flex="1" p="8px" bgColor={theme.colors.gray[500]} justify="center">
						<Heading size="md">Loaded</Heading>
					</Flex>
				</Flex>
				<Flex flex="2">
					<Flex flex="1" p="8px" bgColor={theme.colors.gray[500]} justify="flex-end">
						<Heading size="md">Status:</Heading>
						{state.fields.approval_timestamp ? (
							<Heading size="md" color="green" pl="5px">
								{'Approved - ' + moment(new Date(state.fields.approval_timestamp)).format('M/D/YY')}
							</Heading>
						) : (
							<Heading size="md" color={theme.colors.red[500]} pl="5px">
								Unapproved
							</Heading>
						)}
					</Flex>
				</Flex>
			</Flex>
			{formTree || <Text>This form has no fields</Text>}
			<HStack mt="10px">
				<Button
					size="lg"
					flexGrow={1}
					bg={theme.colors.blue[700]}
					color={theme.colors.white}
					onClick={() => dispatch({ type: 'approve_form' })}
					disabled={
						!validAddons ||
						Boolean(state.fields.approval_timestamp) ||
						(!window.isSupervisor && isSubmitted && !edit)
					}
				>
					Approve
				</Button>
				<Button
					size="lg"
					onClick={onOpen}
					w="200px"
					bg={theme.colors.red[500]}
					_hover={{ background: theme.colors.red[600] }}
					color={theme.colors.white}
					disabled={isSubmitted && !edit}
				>
					Reset
				</Button>
			</HStack>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={cancelResetRef}
				onOpenChange={({ open }) => {
					if (!open) {
						onClose();
					}
				}}
				open={isOpen}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Confirm Reset
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>Are you sure you want to reset all the values on this form to be unchecked?</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button size="lg" ref={cancelResetRef} onClick={onClose}>
										Cancel
									</Button>
								</Dialog.ActionTrigger>
								<Button
									colorPalette="red"
									size="lg"
									ml={3}
									bg={theme.colors.red[500]}
									_hover={{ background: theme.colors.red[600] }}
									color={theme.colors.white}
									onClick={() => {
										dispatch({ type: 'reset_form' });
										onClose();
									}}
									disabled={!window.isSupervisor && isSubmitted && !edit}
								>
									Reset List
								</Button>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</Flex>
	);
};
